import moment from 'moment';

import type { InspirationReference } from '../../../../bridge/types/artifact.ts';

export const PLATFORM_LABEL: Record<
	InspirationReference['content']['platform'],
	string
> = {
	instagram: 'Instagram',
	tiktok: 'TikTok',
	youtube: 'YouTube',
	upload: 'Uploaded design',
};

/**
 * The brief `assemble_design` renders for a `DesignRead` whose every field is
 * empty. It is a mirror of `_EMPTY_BRIEF` in
 * `services/py/shared/utils/design_read.py`, which the server compares against in
 * `is_usable_design_brief`.
 *
 * Copied by hand, because the two rules sit in different repositories and no
 * compiler sees both. Keep them byte for byte the same: if
 * `assemble_design`'s wording changes, this string must change with it.
 */
export const EMPTY_DESIGN_BRIEF =
	"Apply this DESIGN to the user's OWN photo, which is KEPT exactly as shot. " +
	"The design is borrowed from a reference — do NOT copy the reference's own " +
	'photo, subject, scene, lighting, or props. Reproduce the design faithfully ' +
	'and IDENTICALLY on every slide:\n' +
	'- Layout & container: \n' +
	'- Display font (used ONLY for the opening title and the tip numbers): \n' +
	'- Regular/body font (used for headings, supporting copy, and the CTA): ' +
	'same as the display font (single-font design)\n' +
	'- Type sizes:  — match these sizes and keep them IDENTICAL on every slide; ' +
	'do not inflate.\n' +
	'- Text containers / bubbles behind text: none\n' +
	'- Numerals: none\n' +
	'- Palette (use these EXACT colours, no warming or tint drift): \n' +
	'- Treatment: \n' +
	'- Texture / noise: none / clean flat\n' +
	'- Decorative elements: none\n';

/**
 * Whether a stored design read can actually steer a carousel.
 *
 * "The string is non-empty" is a tautology. `assemble_design` renders a long
 * fixed scaffold even for a read that captured nothing, so a record holding
 * only that scaffold passes a non-empty test and is still unusable.
 *
 * Compare against the scaffold itself, never against its length. A terse real
 * read can be SHORTER than the scaffold, because an empty body font falls back
 * to a 43-character sentence.
 *
 * The server holds the authoritative predicate, `is_usable_design_brief`. This
 * is the same rule, so the button and the resolver cannot disagree about the
 * same record.
 */
export const isUsableDesignBrief = (brief: string | undefined): boolean => {
	const text = (brief ?? '').trim();
	return Boolean(text) && text !== EMPTY_DESIGN_BRIEF.trim();
};

/**
 * `why_it_works` as a list, whatever shape the record actually holds.
 *
 * The field is typed `string[]`, and nothing coerces it on the way in: an agent
 * edit stores the value verbatim (`bridge/actions/artifacts.ts`). A record whose
 * reasons an agent rewrote as one sentence therefore arrives as a scalar string.
 * `.filter` and `.join` do not exist on a string, so the read threw, and the
 * only boundary is at the app root — the whole application was replaced by the
 * reload page. ADO-1570 records the same shape on a different field.
 *
 * A scalar is kept as ONE reason rather than dropped. Dropping it would stop the
 * crash and still lose the read the vision call paid for.
 */
export const asReasonList = (value: unknown): string[] => {
	if (typeof value === 'string') return value.trim() ? [value] : [];
	if (!Array.isArray(value)) return [];
	return value.filter((point): point is string => typeof point === 'string');
};

/**
 * The long descriptive reads, each held to its own share of `room`.
 *
 * One long read must never evict another. `imagery_analysis` and
 * `content_analysis` are both unbounded, so rendering them together and cutting
 * the tail let the first one spend the whole budget and delete the second
 * outright — the agent then had no visual read at all, or no caption read.
 *
 * Each read is promised an equal share. A read shorter than its share hands the
 * remainder to the ones after it, so a single long read still uses the whole
 * budget when it is the only one present.
 */
export const renderLongReads = (
	pairs: ReadonlyArray<readonly [string, string | undefined]>,
	room: number,
): string => {
	const present = pairs
		.map(([label, body]) => [label, (body ?? '').trim()] as const)
		.filter(([, body]) => body.length > 0);
	if (present.length === 0 || room <= 0) return '';

	const separator = '\n\n';
	let remaining = room;
	let left = present.length;
	const rendered: string[] = [];
	for (const [label, body] of present) {
		const share = Math.max(Math.floor(remaining / left), 0);
		const text = `${label}: ${body}`.slice(0, share);
		if (text) rendered.push(text);
		remaining -= text.length + separator.length;
		left -= 1;
	}
	return rendered.join(separator);
};

/**
 * Why the saved design cannot start a carousel, and what to do instead.
 *
 * The remedy has to be one the customer has not already tried. An `upload` post
 * IS a picture they gave us, so asking for an upload asks for that same
 * picture again. For a link, the upload path produces the richest read we have.
 *
 * The saved-post card and the content detail page both say this, and the card's
 * test compares the wording character for character. The apostrophe is
 * therefore the U+2019 right single quote, which is what `&rsquo;` renders.
 *
 * `platform` is a plain string, because the card takes the stored value as the
 * server wrote it and an unrecognized value must still get a sentence.
 */
const NO_DESIGN_READ_PREFIX = 'We could not read this post\u2019s design.';

export const noDesignReadMessage = (platform: string): string =>
	platform === 'upload'
		? `${NO_DESIGN_READ_PREFIX} Try a clearer picture.`
		: `${NO_DESIGN_READ_PREFIX} Upload a picture of it to build a carousel ` +
			'in its style.';

export const formatInspirationMeta = (
	content: InspirationReference['content'],
): string => {
	const sharedAt = moment(content.shared_at);
	const sharedOn =
		content.shared_at && sharedAt.isValid()
			? sharedAt.format('MMM D, YYYY')
			: null;
	return [
		PLATFORM_LABEL[content.platform],
		content.media_kind?.trim(),
		sharedOn,
	]
		.filter(Boolean)
		.join(' · ');
};
